'use client';

import { createContext, useContext, useRef, useCallback, ReactNode } from 'react';
import { PostModel } from '../models/posts/Posts';

/**
 * GlobalPostStateContext
 *
 * Centralized coordinator for post-related state updates across different contexts.
 * This ensures state consistency between HomeDataContext and FeedsContext.
 *
 * Architecture:
 * - Implements pub/sub pattern for cross-context communication
 * - All post mutations should go through this context
 * - Child contexts subscribe to updates and sync their local state
 */

// ==================== Types ====================

export enum PostUpdateType {
    FOLLOW_STATUS_CHANGED = 'FOLLOW_STATUS_CHANGED',
    POST_DELETED = 'POST_DELETED',
    POST_UPDATED = 'POST_UPDATED',
    POST_HIDDEN = 'POST_HIDDEN',
    POST_ADDED = 'POST_ADDED',
    POST_FIELD_UPDATED = 'POST_FIELD_UPDATED',
    USER_BLOCKED = 'USER_BLOCKED',
}

export interface FollowStatusPayload {
    userId: string;
    isFollowing: boolean;
}

export interface UserBlockedPayload {
    userId: string;
}

export interface PostDeletedPayload {
    postId: string;
}

export interface PostUpdatedPayload {
    post: PostModel;
}

export interface PostHiddenPayload {
    postId: string;
}

export interface PostAddedPayload {
    post: PostModel;
}

export interface PostFieldUpdatedPayload {
    postId: string;
    field: keyof PostModel;
    value: any;
}

export type PostUpdatePayload =
    | FollowStatusPayload
    | PostDeletedPayload
    | PostUpdatedPayload
    | PostHiddenPayload
    | PostAddedPayload
    | UserBlockedPayload
    | PostFieldUpdatedPayload;

export interface PostUpdate {
    type: PostUpdateType;
    payload: PostUpdatePayload;
    timestamp: number;
    source?: string; // For debugging: which context triggered the update
}

export type UpdateListener = (update: PostUpdate) => void;

// ==================== Context Interface ====================

interface GlobalPostStateContextType {
    // Subscription mechanism
    subscribe: (listener: UpdateListener) => () => void;

    // Actions - these broadcast to all subscribers
    updateUserFollowStatus: (userId: string, isFollowing: boolean, source?: string) => void;
    blockUser: (userId: string, source?: string) => void;
    deletePost: (postId: string, source?: string) => void;
    updatePost: (post: PostModel, source?: string) => void;
    hidePost: (postId: string, source?: string) => void;
    addPost: (post: PostModel, source?: string) => void;
    updatePostField: (postId: string, field: keyof PostModel, value: any, source?: string) => void;

    // Debug - check subscriber count
    getListenerCount: () => number;
}

// ==================== Context Creation ====================

const GlobalPostStateContext = createContext<GlobalPostStateContextType | undefined>(undefined);

// ==================== Provider Implementation ====================

export function GlobalPostStateProvider({ children }: { children: ReactNode }) {
    // Store all subscribed listeners
    const listenersRef = useRef<Set<UpdateListener>>(new Set());

    /**
     * Subscribe to post updates
     * @param listener - Callback function to be called on updates
     * @returns Unsubscribe function
     */
    const subscribe = useCallback((listener: UpdateListener) => {
        listenersRef.current.add(listener);

        // Return unsubscribe function
        return () => {
            listenersRef.current.delete(listener);
        };
    }, []);

    /**
     * Notify all subscribers about an update
     */
    const notify = useCallback((update: PostUpdate) => {
        // Notify all listeners
        listenersRef.current.forEach(listener => {
            try {
                listener(update);
            } catch (error) {
                console.error('[GlobalPostState] Error in listener:', error);
            }
        });
    }, []);

    // ==================== Action Methods ====================

    const updateUserFollowStatus = useCallback((userId: string, isFollowing: boolean, source?: string) => {
        notify({
            type: PostUpdateType.FOLLOW_STATUS_CHANGED,
            payload: { userId, isFollowing },
            timestamp: Date.now(),
            source,
        });
    }, [notify]);
    
    const blockUser = useCallback((userId: string, source?: string) => {
        notify({
            type: PostUpdateType.USER_BLOCKED,
            payload: { userId },
            timestamp: Date.now(),
            source,
        });
    }, [notify]);

    const deletePost = useCallback((postId: string, source?: string) => {
        notify({
            type: PostUpdateType.POST_DELETED,
            payload: { postId },
            timestamp: Date.now(),
            source,
        });
    }, [notify]);

    const updatePost = useCallback((post: PostModel, source?: string) => {
        notify({
            type: PostUpdateType.POST_UPDATED,
            payload: { post },
            timestamp: Date.now(),
            source,
        });
    }, [notify]);

    const hidePost = useCallback((postId: string, source?: string) => {
        notify({
            type: PostUpdateType.POST_HIDDEN,
            payload: { postId },
            timestamp: Date.now(),
            source,
        });
    }, [notify]);

    const addPost = useCallback((post: PostModel, source?: string) => {
        notify({
            type: PostUpdateType.POST_ADDED,
            payload: { post },
            timestamp: Date.now(),
            source,
        });
    }, [notify]);

    const updatePostField = useCallback((postId: string, field: keyof PostModel, value: any, source?: string) => {
        notify({
            type: PostUpdateType.POST_FIELD_UPDATED,
            payload: { postId, field, value },
            timestamp: Date.now(),
            source,
        });
    }, [notify]);

    // Debug helper - get current listener count
    const getListenerCount = useCallback(() => {
        return listenersRef.current.size;
    }, []);

    // ==================== Context Value ====================

    const value: GlobalPostStateContextType = {
        subscribe,
        updateUserFollowStatus,
        blockUser,
        deletePost,
        updatePost,
        hidePost,
        addPost,
        updatePostField,
        getListenerCount,
    };

    return (
        <GlobalPostStateContext.Provider value={value}>
            {children}
        </GlobalPostStateContext.Provider>
    );
}

// ==================== Hook ====================

/**
 * Hook to access GlobalPostState
 *
 * @throws Error if used outside GlobalPostStateProvider
 * @returns GlobalPostStateContextType
 *
 * @example
 * const globalPostState = useGlobalPostState();
 *
 * // Subscribe to updates
 * useEffect(() => {
 *   const unsubscribe = globalPostState.subscribe((update) => {
 *     if (update.type === PostUpdateType.FOLLOW_STATUS_CHANGED) {
 *       // Handle follow status change
 *     }
 *   });
 *   return unsubscribe;
 * }, []);
 *
 * // Dispatch updates
 * globalPostState.updateUserFollowStatus(userId, true, 'MyComponent');
 */
export function useGlobalPostState(): GlobalPostStateContextType {
    const context = useContext(GlobalPostStateContext);

    if (context === undefined) {
        throw new Error('useGlobalPostState must be used within a GlobalPostStateProvider');
    }

    return context;
}
